/**
 * Single source of truth for "is this team currently entitled to paid features?"
 *
 * Before this existed, entitlement was decided from `plan_tier` alone, so a
 * subscription that had lapsed months earlier kept full access to its tier.
 *
 * Deliberate asymmetry in the rules below: `active` and `trialing` stay entitled
 * even when `current_period_end` is in the past. Polar advances that date via
 * webhook, so a late or dropped webhook briefly leaves a genuinely paying team
 * with a stale date. Revoking access there would lock out paying customers over
 * an infrastructure hiccup — a far worse failure than briefly over-serving one.
 * `canceling` is the opposite case: the end date is known and final, so it is
 * enforced.
 */

export interface EntitlementSubscription {
  status?: string | null;
  current_period_end?: string | null;
}

/**
 * Grace window applied to the end of a canceling subscription's period, to
 * absorb clock skew and webhook delay. Access ends this long after the period
 * does.
 */
export const ENTITLEMENT_GRACE_MS = 3 * 24 * 60 * 60 * 1000;

/** Statuses that are entitled for as long as they hold, regardless of dates. */
const ALWAYS_ENTITLED = new Set(['active', 'trialing']);

/** Statuses entitled only until `current_period_end` (plus grace) passes. */
const ENTITLED_UNTIL_PERIOD_END = new Set(['canceling', 'past_due']);

export type EntitlementReason =
  | 'entitled'
  | 'no_subscription'
  | 'period_ended'
  | 'status_not_entitled';

export interface EntitlementResult {
  entitled: boolean;
  reason: EntitlementReason;
  /** When a canceling subscription loses access. Null when not applicable. */
  accessEndsAt: Date | null;
}

export function evaluateEntitlement(
  subscription: EntitlementSubscription | null | undefined,
  now: Date = new Date()
): EntitlementResult {
  if (!subscription?.status) {
    return { entitled: false, reason: 'no_subscription', accessEndsAt: null };
  }

  const status = subscription.status.toLowerCase();

  if (ALWAYS_ENTITLED.has(status)) {
    return { entitled: true, reason: 'entitled', accessEndsAt: null };
  }

  if (ENTITLED_UNTIL_PERIOD_END.has(status)) {
    const end = parseDate(subscription.current_period_end);

    // A canceling row with no end date is indeterminate. Treat it as entitled:
    // the reconcile sweep will set a real status, and guessing "expired" here
    // could cut off someone mid-period.
    if (!end) {
      return { entitled: true, reason: 'entitled', accessEndsAt: null };
    }

    const accessEndsAt = new Date(end.getTime() + ENTITLEMENT_GRACE_MS);
    return accessEndsAt.getTime() > now.getTime()
      ? { entitled: true, reason: 'entitled', accessEndsAt }
      : { entitled: false, reason: 'period_ended', accessEndsAt };
  }

  return { entitled: false, reason: 'status_not_entitled', accessEndsAt: null };
}

/** Convenience wrapper for the common boolean check. */
export function isEntitled(
  subscription: EntitlementSubscription | null | undefined,
  now: Date = new Date()
): boolean {
  return evaluateEntitlement(subscription, now).entitled;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
